// Bridge for minimap drag-to-move-camera (minimap-design.md §5): the gesture lives
// outside the Canvas, the camera controls live inside it. The App drops board-space
// points here; `CameraPanInput` (in the Canvas) drains them per frame and moves the
// orbit target + camera by the same delta, so the grabbed ground point stays under the
// cursor. Instant, no easing — `CameraPanClamp` keeps the pan in bounds afterwards.
//
// Board coordinates again: the in-canvas side multiplies by the Large Board scale
// when applying.

export type BoardPoint = readonly [number, number];

/**
 * A pending grab. It carries BOTH the point the gesture started at and the
 * latest pointer position: the grab's reference (the orbit target at the
 * moment the gesture began) can only be captured in the Canvas, so the begin
 * has to survive until the in-canvas side has seen it — a separate slot for
 * the drag would overwrite it.
 */
interface Pending {
  /** The ground point the gesture grabbed. */
  readonly begin: BoardPoint;
  /** The latest pointer position. */
  readonly point: BoardPoint;
}

interface GrabState {
  /** True between a begin and its cancel. */
  active: boolean;
  /** Board-space ground point grabbed at drag start. */
  startX: number;
  startZ: number;
  /** Orbit target at drag start. */
  targetX: number;
  targetZ: number;
}

export const cameraGrab: GrabState = {
  active: false,
  startX: 0,
  startZ: 0,
  targetX: 0,
  targetZ: 0,
};

let queue: Pending | null = null;
/** The grab point of the running gesture. Kept across drains: the in-canvas
 * side needs it on every frame, but only the first frame captures the target. */
let grabStart: BoardPoint | null = null;

/** A camera drag started: the next drain records the grabbed point + live target. */
export function beginCameraGrab(point: BoardPoint): void {
  grabStart = point;
  queue = { begin: point, point };
}

/** The pointer moved: keep the grabbed point under the cursor. */
export function dragCameraTo(point: BoardPoint): void {
  // A drag with no recorded begin (the hook always sends begin first) is ignored.
  if (!cameraGrab.active && queue === null) return;
  queue = { begin: grabStart ?? point, point };
}

/** Take the queued request (the in-canvas consumer runs once per frame). */
export function takeCameraInput(): Pending | null {
  const q = queue;
  queue = null;
  return q;
}

export function cancelCameraGrab(): void {
  cameraGrab.active = false;
  grabStart = null;
  queue = null;
}

/**
 * Pure grab math: where the orbit target must go so the ground point grabbed at
 * `start` stays under a cursor now at `current` (target started at `target`).
 */
export function grabTarget(start: BoardPoint, current: BoardPoint, target: BoardPoint): BoardPoint {
  return [target[0] + (start[0] - current[0]), target[1] + (start[1] - current[1])];
}
